import { useState } from "react";
import type { RetrievalSearchResponse } from "../services/api";

interface Props {
  onSearch: (query: string) => Promise<RetrievalSearchResponse>;
}

export function RetrievalPanel({ onSearch }: Props) {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<RetrievalSearchResponse | null>(null);

  const submit = async () => {
    const q = query.trim();
    if (!q) return;
    setLoading(true);
    setError(null);
    try {
      setResult(await onSearch(q));
    } catch (err) {
      setError((err as Error).message || "retrieval failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      data-testid="retrieval-panel"
      style={{
        background: "var(--color-surface-elevated)",
        border: "1px solid var(--color-border)",
        borderRadius: "var(--radius-8)",
        padding: "var(--space-12)",
      }}
    >
      <header style={{ marginBottom: "var(--space-8)" }}>
        <h3 className="type-h3" style={{ margin: 0, fontSize: 13 }}>
          Retrieved context · advisory only
        </h3>
        <p className="type-caption" style={{ margin: "var(--space-4) 0 0 0", color: "var(--color-text-secondary)" }}>
          Search RAGLight-shaped context. Results help reviewer navigation and do not update canonical rules or legal references.
        </p>
      </header>

      <div style={{ display: "flex", gap: "var(--space-8)", alignItems: "center" }}>
        <label style={{ fontSize: 12, color: "var(--color-text-secondary)", flex: 1 }}>
          Retrieval query
          <input
            aria-label="Retrieval query"
            value={query}
            onChange={(e) => setQuery(e.currentTarget.value)}
            placeholder="e.g. AI Act Article 5, R156 SUMS, battery passport"
            style={{
              marginTop: 4,
              width: "100%",
              padding: "var(--space-8)",
              border: "1px solid var(--color-border-strong)",
              borderRadius: "var(--radius-4)",
              background: "var(--color-surface)",
              color: "var(--color-text-primary)",
            }}
          />
        </label>
        <button
          onClick={submit}
          disabled={loading || query.trim().length === 0}
          style={{
            marginTop: 18,
            padding: "var(--space-8) var(--space-12)",
            border: "1px solid var(--color-border-strong)",
            borderRadius: "var(--radius-4)",
            background: "var(--color-surface)",
            cursor: loading ? "wait" : "pointer",
            fontWeight: 700,
          }}
        >
          {loading ? "Searching..." : "Search retrieved context"}
        </button>
      </div>

      {error && (
        <p role="alert" style={{ color: "var(--status-candidate-blocked)", fontSize: 12 }}>
          {error}
        </p>
      )}

      {result && (
        <div style={{ marginTop: "var(--space-12)", display: "grid", gap: "var(--space-8)" }}>
          <p className="type-caption" style={{ margin: 0, color: "var(--color-text-secondary)" }}>
            {result.count} result(s). {result.disclaimer}
          </p>
          {result.results.map((ctx) => (
            <article
              key={ctx.chunkId}
              style={{
                border: "1px solid var(--color-border)",
                borderLeft: "4px solid var(--ft-ecosystem)",
                borderRadius: "var(--radius-4)",
                padding: "var(--space-8)",
                background: "var(--color-surface)",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", gap: "var(--space-8)" }}>
                <strong style={{ fontSize: 13 }}>{ctx.title}</strong>
                <span style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>
                  {ctx.retrievalMethod} · rank {ctx.rank}
                </span>
              </div>
              <p style={{ margin: "var(--space-8) 0", fontSize: 12 }}>{ctx.snippet}</p>
              <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--color-text-secondary)" }}>
                {ctx.chunkId} · {ctx.locator.label} · sha256={ctx.rawSha256.slice(0, 12)}
              </div>
              <a href={ctx.sourceUrl} target="_blank" rel="noreferrer" style={{ fontSize: 11 }}>
                Source document
              </a>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
